<template>
  <MainLayout scroll-toolbar :bg-color="'white'">
    <template #toolbar>
      <div v-if="!spyderPaywall.show"
      class="relative flex items-center justify-between px-5 py-3.5 border-b border-border-normal">
        <div class="flex items-center gap-3">
          <img src="../../assets/icons/spyder.png" class="w-10 h-10">
          <BaseText type="label" size="md" class="text-neutral-900 font-medium">
            Spyder
          </BaseText>
        </div>

        <div class="group flex items-center gap-1.5 w-full search-container px-2 text-sm rounded-md search-input-container"
        @mouseenter="searchBarHovered = true" @mouseleave="searchBarHovered = false">
          <DynamicSearchIcon class="flex-shrink-0" :is-hovered="searchBarHovered || isSearchFocussed" />
          <!-- Search Input -->
          <input v-model="textSearch" type="text" class="w-full search-input outline-none border-none text-black h-8"
          placeholder="Search brands..." @focus="isSearchFocussed = true" @blur="isSearchFocussed = false">
          <BaseLoadingSpinnerCircle v-if="loading.search" class="text-icon-normal" />
        </div>

        <div class="flex items-center gap-3">
          <button class="flex items-center gap-1.5 pl-1 py-1 pr-3 rounded-full border border-border-normal"
          style="transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out, opacity 100ms ease-in-out;"
          :class="[loadingBrands ? 'opacity-0' : 'opacity-100', { 'cursor-default': isAnnualPlan }]" 
          :disabled="isAnnualPlan" @click="showBuySpyderCreditsModal = true">
            <img v-if="getSpyderSubscriptions.length > 0" src="../../assets/icons/spyder-token.png" class="w-5 h-5">
            <img v-else src="../../assets/icons/spyder-token-disabled.png" class="w-5 h-5">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ getSpyderSubscriptions?.length }}/{{ getSpyderLimit }} credits
            </BaseText>
          </button>
          <BaseButton primary icon-only @click="showModal = true">
            <PlusIcon stroke="white" />
          </BaseButton>
        </div>
      </div>
      <!-- Subview Tabs -->
      <div v-if="!spyderPaywall.show" class="flex gap-2 w-full px-5 py-2 border-b border-border-normal">
        <router-link :to="{ name: 'SpyderBrandsView' }" class="spyder-view-tab" :class="{ 'active': selectedTab === 'brands' }">
          <SpyderIcon class="w-5 h-5" />
          Tracked Brands
        </router-link>
        <router-link :to="{ name: 'SpyderFeedView' }" class="spyder-view-tab" :class="{ 'active': selectedTab === 'feed' }">
          <CommunityFeedIcon class="w-5 h-5" />
          Feed
        </router-link>
      </div>
    </template>
    <template #content>
      <div class="bg-white" :class="spyderPaywall.show ? '' : 'mb-10'">
        <BaseLoadingLogo v-if="getSpyderSubscriptionsLoading || loadingBrands" />
        <!-- SPYDER PAYWALL VIEW -->
        <SpyderPaywallState v-else-if="!getSpyderSubscriptionsLoading && !loadingBrands && spyderPaywall.show" 
        class="-mt-4" :subscriptionType="spyderPaywall" />
        <!-- =============== FIRST TIME WELCOME VIEW =============== -->
        <div v-else-if="!getSpyderSubscriptionsLoading && !loadingBrands && getSpyderSubscriptions?.length === 0">
          <SpyderBrandsWelcomeView @openAddSpyderModal="initialModalView = $event, showModal = true"
          @openTutorialModal="tutorialTimestamp = 21, showTutorialModal = true" />
        </div>
        <!-- =============== MAIN VIEW ================ -->
        <div v-else-if="!(getSpyderSubscriptions?.length === 0 && !getSpyderSubscriptionsLoading) && !loadingBrands"
        class="flex flex-col gap-5 w-full">
          <div v-for="(folder, index) in sortedFolders" :key="`folder-${index}`" :ref="`folder-${folder.id}`"
          class="rounded-xl border border-border-normal">
            <div class="folder-header-grid-template w-full py-2.5">
              <div class="flex items-center gap-3 py-1.5 ml-4">
                <FolderIcon class="text-text-subdued flex-shrink-0" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  {{ folder.name }}
                </BaseText>
              </div>
              <div class="flex items-center gap-3 px-3">
                <div class="relative">
                  <div class="cursor-default" @mouseenter="hoverFolderBrandInfo(index, true)"
                  @mouseleave="hoverFolderBrandInfo(index, false)">
                    <BaseText type="body" size="sm" class="text-text-normal">
                      {{ folderBrandCounts[index] }} Spyder Brands
                    </BaseText>
                  </div>
                  <transition>
                    <div v-if="!folderExpanded[index] && folderBrandInfoHovered[index]"
                    class="brand-info-tooltip flex flex-col gap-1 p-1 bg-neutral-700 rounded-lg w-max shadow-md"
                    style="min-width: 130px; max-width: 250px">
                      <div v-for="(brand, brandIndex) in getBrandsInFolder(folder.id, last_changed, textSearch).slice(0, 7)"
                        :key="`brand-tooltip-row-${brand.id}-${brandIndex}`" class="flex items-center gap-2 p-1">
                        <img :src="brand.avatar" class="w-6 h-6" style="border-radius: 5px">
                        <BaseText type="label" size="sm" class="text-white truncate">
                          {{ brand.name }}
                        </BaseText>
                      </div>
                      <div v-if="folderBrandCounts[index] > 7" class="flex items-center pt-0.5 pb-1.5 pl-1.5 pr-1">
                        <BaseText type="body" size="sm" class="text-text-subdued">
                          +{{ folderBrandCounts[index] - 7 }} more
                        </BaseText>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <BaseText type="body" size="sm" class="text-neutral-400">
                  Created on
                </BaseText>
                <BaseText type="body" size="sm">
                  {{ new Date(folder.createdAt || defaultCreatedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long', day: 'numeric'
                  }) }}
                </BaseText>
                <button v-if="folderBrandCounts[index] > 0" class="ml-auto mr-5 transition-transform duration-200"
                  :style="{ transform: folderExpanded[index] ? 'scaleY(-1)' : 'scaleY(1)' }"
                  @click="expandFolder(index)">
                  <ChevronIcon :width="24" :height="24" class-name="text-icon-normal" />
                </button>
              </div>
            </div>
            <div :ref="`brand-folder-content-${index}`"
            :style="{ height: folderExpanded[index] ? `${(folderBrandCounts[index] + (folderBrandCounts[index] > 0 ? 1 : 0)) * 44}px` : '0px' }"
            class="brand-folder-content flex flex-col gap-2 px-2 w-full">
              <!-- Brands table header -->
              <div v-if="folderBrandCounts[index] > 0" class="brand-grid-template rounded-lg bg-neutral-10">
                <div class="group pl-12 pr-4 py-2 cursor-pointer flex items-center gap-1"
                @click="setSort('name', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Name
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" 
                  :style="{
                    transform: showDescIcon('name', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'name' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="last-checked-column px-3 py-2 cursor-pointer flex gap-1 items-center group"
                @click="setSort('last_checked', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Last checked
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" 
                  :style="{
                    transform: showDescIcon('last_checked', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'last_checked' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="data-since-column px-3 py-2 cursor-pointer flex items-center gap-1 group"
                @click="setSort('data_since', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Data Since
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" :style="{
                    transform: showDescIcon('data_since', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'data_since' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="px-3 py-2 cursor-pointer flex items-center gap-1 group"
                @click="setSort('ad_count', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Ads
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" :style="{
                    transform: showDescIcon('ad_count', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'ad_count' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="px-3 py-2">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Type
                  </BaseText>
                </div>
              </div>
              <!-- Brands rows -->
              <div v-for="(brand, brandIndex) in getSortedBrandsInFolder(folder.id)"
              :key="`brand-${brand.id}-${brandIndex}`" class="w-full" :class="{ 'cursor-not-allowed': !brand.id }">
                <router-link :to="{ name: 'SpyderView', params: { id: brand.id, view: 'meta', mode: 'ad-library' } }"
                class="brand-grid-template pr-0.5 cursor-pointer rounded-lg transition-colors hover:bg-neutral-10"
                :disabled="!brand.id" :event="brand.id ? 'click' : ''"
                :class="{ 'bg-neutral-10': expandedBrandOptions === brand.id }">
                  <div class="flex items-center gap-3 px-2 py-1">
                    <div class="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                      <img v-if="brand.avatar" :src="brand.avatar" class="w-7 h-7 rounded-md">
                      <AnalyticsSpinnerIcon v-else class="ml-2 mt-2" />
                    </div>
                    <BaseText type="label" size="sm" class="truncate"
                    :class="brand.id ? 'text-text-muted' : 'text-text-disabled'"
                    :style="brand.id ? '' : 'font-weight: 400;'">
                      {{ brand.name || 'In Progress...' }}
                    </BaseText>
                    <div v-if="lastAddedBrands.includes(brand.subscription.pageId)" class="new-brand-tag">
                      <BaseText size="xs" type="label">
                        New
                      </BaseText>
                    </div>
                  </div>
                  <div class="last-checked-column px-3 py-2">
                    <template v-if="brand.name">
                      <BaseText v-if="formatLastChecked(brand?.subscription)" type="body" size="sm" class="text-text-subdued">
                        {{ formatLastChecked(brand?.subscription) }} ago
                      </BaseText>
                      <div v-else class="skeleton h-3 w-12 rounded" />
                    </template>
                    <AnalyticsSpinnerIcon v-else />
                  </div>
                  <div class="data-since-column px-3 py-2">
                    <BaseText type="body" size="sm" class="text-text-subdued">
                      {{ formatDate(brand?.subscription?.createdAt) }}
                    </BaseText>
                  </div>
                  <div class="ad-status-grid-template px-3 py-2">
                    <div class="flex items-center gap-2 whitespace-nowrap">
                      <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ backgroundColor: brand.analytics?.brandDisplayFormatCounts ? '#00A879' : '#DFE1E7' }" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.liveAdCounts?.active_count?.toLocaleString() || 0 }}</span>
                        <span class="text-text-normal"> Active</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-2 whitespace-nowrap">
                      <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: #DFE1E7;" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.liveAdCounts?.inactive_count?.toLocaleString() || 0 }}</span>
                        <span class="text-text-normal"> Inactive</span>
                      </BaseText>
                    </div>
                  </div>
                  <div class="format-breakdown-grid-template px-3 py-2">
                    <div class="flex items-center gap-1.5">
                      <VideoFormatIcon :width="16" :height="16" :stroke="brand.analytics?.brandDisplayFormatCounts ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.brandDisplayFormatCounts?.video?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <ImageFormatIcon :width="16" :height="16" :stroke="brand.analytics?.brandDisplayFormatCounts ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.brandDisplayFormatCounts?.image?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <CarouselFormatIcon :width="16" :height="16" :stroke="brand.analytics?.brandDisplayFormatCounts ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.brandDisplayFormatCounts?.carousel?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <DynamicFormatIcon :width="16" :height="16" :stroke="brand.analytics?.brandDisplayFormatCounts ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.brandDisplayFormatCounts?.dynamic?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <OtherFormatIcon :width="16" :height="16" :stroke="brand.analytics?.brandDisplayFormatCounts ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="getBrandAnalyticsLoading" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.brandDisplayFormatCounts?.other?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <button v-on-clickaway="() => { expandedBrandOptions = null }"
                    class="relative brand-menu-button p-0.5 rounded-md z-20"
                    :class="expandedBrandOptions === brand.id ? 'active' : 'inactive'"
                    :disabled="loadingMenu === brand?.subscription?.pageId"
                    @click.stop.prevent="expandedBrandOptions = expandedBrandOptions === brand.id ? null : brand.id">
                      <BaseLoadingSpinnerCircle v-if="loadingMenu === brand?.subscription?.pageId" class="text-icon-normal"/>
                      <KebabMenuIcon v-else />
                      <transition name="option">
                        <div v-if="expandedBrandOptions === brand.id"
                        class="brand-options flex flex-col gap-0.5 p-1 rounded-lg bg-white">
                          <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                          @click.stop.prevent="expandedBrandOptions = null, showChangeBrandFolderModal(brand)">
                            <ChangeFolderIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                            <BaseText type="body" size="sm"
                              class="transition-colors text-text-normal group-hover:text-text-muted">
                              Move
                            </BaseText>
                          </button>
                          <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                          @click.stop.prevent="expandedBrandOptions = null, removeSpyderBrand(brand)">
                            <DeleteIcon class-name="transition-colors text-icon-normal group-hover:text-icon-muted" />
                            <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                              Remove
                            </BaseText>
                          </button>
                        </div>
                      </transition>
                    </button>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
          <button class="flex items-center justify-center gap-2 w-full py-2 rounded-xl transition-colors hover:bg-neutral-25"
          @click="showFolderModal = true">
            <PlusIcon stroke="#5E6678" />
            <BaseText type="label" size="sm" class="text-text-normal">
              Create new folder
            </BaseText>
          </button>
        </div>

        <AddSpyderModal 
          v-if="showModal" 
          :initial-view="initialModalView" 
          :spyder-subscriptions="getSpyderSubscriptions"
          :spyder-limit="spyderLimit" 
          :page-id-to-add="modalPageIdToAdd" 
          :initial-folder="modalInitialFolder"
          @spyderAdded="handleAddSpyder" 
          @close="showModal = false, initialModalView = 'search'"
          @buyCredits="showBuySpyderCreditsModal = true"
          @openTutorialModal="tutorialTimestamp = 58, showTutorialModal = true" 
          @scrollFolder="handleScrollTo" 
        />
        <BuySpyderCreditsModal 
          v-if="showBuySpyderCreditsModal && !isAnnualPlan"
          :current-sub-count="getSpyderSubscriptions?.length" 
          :spyder-limit="getSpyderLimit"
          @close="showBuySpyderCreditsModal = false" 
        />
        <FolderModal 
          v-if="showFolderModal" 
          @close="showFolderModal = false" 
        />
        <ChangeFolderModal 
          v-if="showChangeFolderModal" 
          :item="changeFolderBrand" item-type="brand"
          @close="showChangeFolderModal = false, changeFolderBrand = null" 
          @onFolderMove="last_changed = +new Date()" 
        />
        <BaseTutorialModal 
          v-if="showTutorialModal"
          video-url="https://www.youtube.com/embed/zNj71_-IIVk?si=C5PRycUlLRkX86l8" 
          :timestamp="tutorialTimestamp"
          @close="showTutorialModal = false" 
        />
        <FixSpyderModal
          v-if="showSpyderFixModal"
          :spyder-limit="spyderLimit"
          @close="showSpyderFixModal = false"
        />
        <SpyderBrandModal 
          v-if="showBrandPreviewModal" 
          :page-id="modalPageIdToAdd"
          @close="showBrandPreviewModal = false, modalPageIdToAdd = ''" 
          @spyderAdded="handleAddSpyder" 
        />
      </div>
    </template>
  </mainlayout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { mixin as clickaway } from 'vue-clickaway2'
import SpyderBrandsWelcomeView from '../../components/spyderV2/SpyderBrandsWelcomeView.vue'
import firebase from '../../api/config/FirebaseConfig'
import FirebaseAPI from '@/api/firebase'
import ForeplayAPI from '@/api/foreplayServer'
import { memoize } from 'lodash'
// Components
import MainLayout from '../../layouts/MainLayout.vue'
import FolderModal from '../../components/sidebar/FolderModal.vue'
import ChangeFolderModal from '../../components/globals/ChangeFolderModal.vue'
import AddSpyderModal from '../../components/spyderV2/AddSpyderModal.vue'
import BuySpyderCreditsModal from '../../components/spyderV2/BuySpyderCreditsModal.vue'
import SpyderPaywallState from "../../components/spyderV2/SpyderPaywallState.vue"
import FixSpyderModal from '../../components/spyderV2/FixSpyderModal.vue'
import SpyderBrandModal from '../../components/spyderV2/SpyderBrandModal.vue'

// Icons
import FolderIcon from '../../components/globals/Icons/SpyderV2Icons/FolderIcon.vue'
import VideoFormatIcon from '../../components/globals/Icons/FilterIcons/VideoFormatIcon.vue'
import ImageFormatIcon from '../../components/globals/Icons/FilterIcons/ImageFormatIcon.vue'
import CarouselFormatIcon from '../../components/globals/Icons/FilterIcons/CarouselFormatIcon.vue'
import DynamicFormatIcon from '../../components/globals/Icons/FilterIcons/DynamicFormatIcon.vue'
import OtherFormatIcon from '../../components/globals/Icons/FilterIcons/OtherFormatIcon.vue'
import AnalyticsSpinnerIcon from '../../components/globals/Icons/SpyderV2Icons/AnalyticsSpinnerIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'
import PlayIcon from '../../components/globals/Icons/PlayIcon.vue'
import PlusIcon from '../../components/globals/Icons/PlusIcon.vue'
import KebabMenuIcon from '../../components/globals/Icons/KebabMenuIcon.vue'
import ChangeFolderIcon from '../../components/globals/Icons/ChangeFolderIcon.vue'
import DeleteIcon from '../../components/globals/Icons/DeleteIcon.vue'
import UpArrowIcon from '../../components/globals/Icons/SearchIcons/UpArrowKeyIcon.vue'
import DynamicSearchIcon from '../../components/globals/Icons/SearchIcons/DynamicSearchIcon.vue'
import SpyderIcon from '../../components/globals/Icons/SidebarIcons/SpyderIcon.vue'
import CommunityFeedIcon from '../../components/globals/Icons/DiscoveryIcons/CommunityFeedIcon.vue'

export default {
  name: 'SpyderBrandsView',
  components: {
    AddSpyderModal,
    AnalyticsSpinnerIcon,
    BuySpyderCreditsModal,
    CarouselFormatIcon,
    ChangeFolderIcon,
    ChangeFolderModal,
    ChevronIcon,
    DeleteIcon,
    DynamicFormatIcon,
    CommunityFeedIcon,
    FolderIcon,
    FolderModal,
    ImageFormatIcon,
    KebabMenuIcon,
    MainLayout,
    OtherFormatIcon,
    PlayIcon,
    PlusIcon,
    SpyderBrandsWelcomeView,
    SpyderIcon,
    VideoFormatIcon,
    UpArrowIcon,
    SpyderPaywallState,
    DynamicSearchIcon,
    FixSpyderModal,
    SpyderBrandModal,
  },
  mixins: [clickaway],
  data() {
    return {
      brandAnalytics: [],
      loadingBrands: true,
      brands: [],
      subscriptions: [],
      favoritedBrands: [],
      infiniteId: +new Date(),
      defaultCreatedDate: new Date(),
      showModal: false,
      initialModalView: 'search',
      modalPageIdToAdd: '',
      modalInitialFolder: { id: null, name: 'Default Folder' },
      showFolderModal: false,
      folderExpanded: [],
      folderBrandInfoHovered: [],
      showTutorialModal: false,
      tutorialTimestamp: 58,
      pastWeekAdsByBrands: {},
      doneLoadingLastWeekStats: false,
      textSearch: '',

      loading: {
        search: false,
      },
      loadingMenu: null, // pageId of brand loading
      isSearchFocussed: false,
      searchBarHovered: false,

      // Spyder Subscription (new)
      showBuySpyderCreditsModal: false,
      showSpyderFixModal: false,
      showBrandPreviewModal: false,

      // Spyder Subscription
      showManageBoardModal: false,
      showChangeFolderModal: false,
      showAdvertisementModal: false,
      spyderLimit: 2,
      expandedBrandOptions: null,
      changeFolderBrand: null,
      lastAddedBrands: [],

      // Intervals
      brandIntervals: [],

      last_changed: +new Date(),

      sortType: {
        key: 'name',
        value: 'asc',
        folder: null
      }
    }
  },
  computed: {
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getStripeCustomer', 'getUserSubscription', 'getTeamSubscription', 'getUserFreeTrialDate', 'isFreeTier', 'getTeamSubscriptionState', 'getSpyderLimit', 'getIsLegacy']),
    ...mapGetters('BoardsModule', ['getFolders', 'getInitialFoldersLoaded']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getSpyderSubscriptionsLoading', 'getBrandAnalytics', 'getBrandAnalyticsLoading']),
    isAnnualPlan() {
      return this.getUserSubscription?.interval === 'year'
    },
    spyderPaywall() {
      const userSubscription = this.getUserSubscription
      const isBasic = userSubscription.internalProductId === 'basic_plan'
      const isLegacy = this.getIsLegacy
      return {
        show: ((isBasic && this.getSpyderLimit === 0) || (isLegacy && this.getSpyderLimit === 0) || this.getSpyderLimit === 0),
        isLegacy,
        isBasic
      }
    },
    sortedFolders() {
      // We want empty folders (besides the default folder) to be at the bottom
      const nonEmptyFolders = this.getFolders.filter(folder => (folder?.spyderBrands?.length || 0) > 0)
      return [{ id: null, name: 'Default Folder' }, ...nonEmptyFolders]
    },
    hasSubscription() {
      // check if a week has passed since trial and if they're free tier
      if (this.getTeam && !['past_due', 'canceled'].includes(this.getTeamSubscriptionState)) {
        return true
      }

      return !this.isFreeTier
    },
    folderBrandCounts() {
      return [this.getBrandsInFolder(null, this.last_changed, this.textSearch).length, ...this.sortedFolders.slice(1).map(folder => this.getBrandsInFolder(folder.id, this.last_changed, this.textSearch).length || 0)]
    },
    selectedTab () {
      return this.$route.name === 'SpyderFeedView' ? 'feed' : 'brands'
    }
  },
  watch: {
    getFolders: { // Ensure all the folders have an expanded/hover state mapped in the array (+1 for default)
      immediate: true,
      handler() {
        this.folderExpanded = [...this.getFolders.map(() => true), true]
        this.folderBrandInfoHovered = [...this.getFolders.map(() => false), false]
        this.removeTransitionListeners()
        this.attachTransitionListeners()
      }
    },
    getBrandAnalytics: {
      immediate: true,
      handler() {
        this.last_changed = +new Date()
      }
    }
  },
  async mounted() {
    try {
      this.checkSpyderOverage()
      this.spyderLimit = this.getSpyderLimit;

      if (this.spyderPaywall.show) {
        this.loadingBrands = false;
        return;
      }
      // Wait for folders to load before fetching spyder subscriptions and brand analytics
      await this.waitForFolders()

      await Promise.all([
        this.fetchSpyderSubscriptions(),
        this.fetchBrandAnalytics() // Fetch analytics in parallel
      ])

    } catch (error) {
      this.spyderLimit = 2
      console.error(error)
      location.reload()
    }

    // Get the account's created date (used for the default folder's "Created on" date)
    const userCreated = firebase.auth().currentUser.metadata.creationTime
    this.defaultCreatedDate = new Date(userCreated)

    // Idk what these were being used for, but im adding a pageId query param that will preload the brand for the user to add
    if (this.$route.query.id) {
      this.initialModalView = 'manual'
      this.modalPageIdToAdd = this.$route.query.id
      this.showModal = true
      history.replaceState({}, null, this.$route.path) // Clear the query params
    } else if (this.$route.query.folderId) {
      const folderId = this.$route.query.folderId
      if (folderId !== 'default') this.modalInitialFolder = this.getFolders.find(folder => folder.id === folderId)
      this.showModal = true
      history.replaceState({}, null, this.$route.path) // Clear the query params
    } else if (this.$route.query.pageId) {
      this.showBrandPreviewModal = true
      this.modalPageIdToAdd = this.$route.query.pageId
      history.replaceState({}, null, this.$route.path) // Clear the query params
    }

    this.loadingBrands = false

    this.pollUnprocessedBrands()
    this.attachTransitionListeners()
  },
  beforeDestroy() {
    this.brandIntervals.forEach(interval => clearInterval(interval))
    this.removeTransitionListeners()
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions', 'fetchBrandAnalytics']),
    ...mapActions('AuthModule', ['setUserSubscription']),
    ...mapActions('BoardsModule', ['fetchFolders']),
    ...mapMutations('BoardsModule', ['SET_FOLDERS']),
    ...mapMutations('SpyderModule', ['SET_SPYDER_SUBSCRIPTIONS']),
    async checkSpyderOverage() {
      try{
        const { data: hasOverage } = await ForeplayAPI.Spyder.checkSpyderOverage()
        this.showSpyderFixModal = hasOverage || false
      } catch (error) {
        console.error(error)
      }
    },
    async waitForFolders() {
      if (this.getInitialFoldersLoaded && this.getFolders.length > 0) {
        return true
      }

      return new Promise((resolve) => {
        let attempts = 0
        const maxAttempts = 10

        const interval = setInterval(() => {
          attempts++

          if (this.getInitialFoldersLoaded && this.getFolders.length > 0) {
            clearInterval(interval)
            resolve(true)
          } else if (attempts >= maxAttempts) {
            clearInterval(interval)
            resolve(false)
          }
        }, 100)
      })
    },
    setSort(key, folderId) {
      const sameKey = this.sortType.key === key
      const sameFolder = this.sortType.folder === folderId
      const isAsc = this.sortType.value === 'asc'
      this.sortType = {
        key,
        value: (sameKey && sameFolder) ? (isAsc ? 'desc' : 'asc') : 'asc',
        folder: folderId
      }
    },
    showDescIcon(key, folderId) {
      return this.sortType.folder === folderId && this.sortType.key === key && this.sortType.value === 'desc'
    },
    getSortedBrandsInFolder(folderId) {
      const brands = this.getBrandsInFolder(folderId, this.last_changed, this.textSearch)
      const sortedBrands = brands.sort((a, b) => this.getSortCompare(a, b, folderId))
      return sortedBrands
    },
    getSortCompare(a, b, folderId) {
      const { key, value } = this.sortType
      const isAsc = value === 'asc'
      let comparison = 0

      if (key === 'name') {
        // Handle cases where name might be undefined
        const nameA = a[key] || ''
        const nameB = b[key] || ''
        comparison = nameA.localeCompare(nameB)
      }
      if (key === 'last_checked') {
        comparison = -1 * ((this.getLastCheckedSeconds(a.subscription) || 0) - (this.getLastCheckedSeconds(b.subscription) || 0))
      }
      if (key === 'data_since') {
        comparison = (a.subscription?.createdAt || 0) - (b.subscription?.createdAt || 0)
      }
      if (key === 'ad_count') {
        comparison = (a.analytics?.liveAdCounts?.active_count || 0) - (b.analytics?.liveAdCounts?.active_count || 0)
      }

      return this.sortType.folder === folderId ? isAsc ? comparison : -comparison : 0
    },
    pollUnprocessedBrands() {
      const unprocessedBrands = this.getSpyderSubscriptions.filter(subscription => !subscription.subscription?.brandId)
      console.log('unprocessedBrands', unprocessedBrands)

      // Poll unprocessedBrands
      for (const spyderBrand of unprocessedBrands) {
        const intervalId = setInterval(async () => {
          // Check by page ID (fb_ad_page_track ID isn't available here)
          try {
            const spyderBrandData = await ForeplayAPI.Spyder.getSubscription({ pageId: spyderBrand.subscription.pageId })

            if (spyderBrandData?.brandId) {
              clearInterval(intervalId)

              // Merge the brand and the subscription
              const brand = await FirebaseAPI.Brands.get(spyderBrandData.brandId)

              const updatedSubscription = { ...spyderBrandData, ...brand }
              const filteredSubscriptions = this.getSpyderSubscriptions.filter(
                subscription => subscription.subscription.pageId !== updatedSubscription.pageId
              )

              this.SET_SPYDER_SUBSCRIPTIONS([...filteredSubscriptions, updatedSubscription])
            }
          } catch (error) {
            console.error(error)
          }
        }, 1500)

        this.brandIntervals.push(intervalId)
      }
    },
    closeSpyderModal() {
      this.showModal = false
    },
    openAdModal() {
      this.manualUpload = false
      this.showAdvertisementModal = true
    },
    expandFolder(index) {
      this.$refs[`brand-folder-content-${index}`][0].style.overflow = 'hidden'
      this.folderExpanded = this.folderExpanded.map((expanded, i) => i === index ? !expanded : expanded)
    },
    hoverFolderBrandInfo(index, isHovered) {
      this.folderBrandInfoHovered = this.folderBrandInfoHovered.map((hovered, i) => i === index ? isHovered : hovered)
    },
    formatTime(time) {
      if (!time || isNaN(time)) return null
      const updatedAgo = ((new Date().getTime() - (time * 1000)) / 3600000)
      return updatedAgo < 1 ? `${Math.floor(updatedAgo * 60)}m` : `${Math.floor(updatedAgo)}h`
    },
    getLastCheckedSeconds(subscription) {
      const lc = subscription?.last_checked
      return lc?._seconds ?? lc?.seconds ?? null
    },
    formatLastChecked(subscription) {
      return this.formatTime(this.getLastCheckedSeconds(subscription))
    },
    formatDate(dateMS) {
      const date = new Date(dateMS)
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return date.toLocaleDateString('en-US', options)
    },
    getBrandsInFolder: memoize(function (folderId, _, textSearch = '') {
      // Helper function for deep cloning
      const deepClone = (obj) => JSON.parse(JSON.stringify(obj))

      // If folderId is null, get all brands not in a folder (Default Folder)
      if (!folderId) {
        const brands = this.getSpyderSubscriptions.filter(subscription =>
          !this.getFolders.find(folder => folder?.spyderBrands?.includes(subscription.subscription.pageId))
        )
        const mappedBrands = brands.map(brand => {
          // Deep clone the brand
          const clonedBrand = deepClone(brand)

          // Use getBrandAnalytics from Vuex store
          const analytics = this.getBrandAnalytics.find(analytics => analytics.pageId === clonedBrand.adLibraryId)

          clonedBrand.analytics = {
            ...analytics,
            liveAdCounts: {
              ...analytics?.liveAdCounts,
              active_count: analytics?.liveAdCounts?.active_count || 0,
              inactive_count: analytics?.liveAdCounts?.inactive_count || 0
            }
          }

          if (!clonedBrand.analytics || !clonedBrand.analytics.brandDisplayFormatCounts) return clonedBrand
          const brandDisplayFormatCounts = clonedBrand.analytics.brandDisplayFormatCounts || {}

          // Map API format keys → display buckets
          clonedBrand.analytics.brandDisplayFormatCounts.carousel = brandDisplayFormatCounts.carousel || 0
          clonedBrand.analytics.brandDisplayFormatCounts.dynamic = brandDisplayFormatCounts.dpa || 0
          clonedBrand.analytics.brandDisplayFormatCounts.other = brandDisplayFormatCounts.page_like || 0

          return clonedBrand
        })
        return textSearch.length > 0 ? mappedBrands.filter(brand => brand.name.toLowerCase().includes(textSearch.toLowerCase())) : mappedBrands
      }

      // Get brands in folder
      const folder = this.getFolders.find(folder => folder.id === folderId)
      if (!folder.spyderBrands) return []

      return folder.spyderBrands.map(brandId => {
        const brand = this.getSpyderSubscriptions.find(subscription => subscription.subscription.pageId === brandId)

        if (!brand) return null

        // Deep clone the brand
        const clonedBrand = deepClone(brand)

        const analytics = this.getBrandAnalytics.find(analytics => analytics.pageId === clonedBrand?.adLibraryId)

        clonedBrand.analytics = {
          ...analytics,
          liveAdCounts: {
            ...analytics?.liveAdCounts,
            active_count: analytics?.liveAdCounts?.active_count || 0,
            inactive_count: analytics?.liveAdCounts?.inactive_count || 0
          }
        }

        if (!clonedBrand?.analytics || !clonedBrand.analytics.brandDisplayFormatCounts) return clonedBrand

        const brandDisplayFormatCounts = clonedBrand.analytics?.brandDisplayFormatCounts || {}

        // Map API format keys → display buckets
        clonedBrand.analytics.brandDisplayFormatCounts.carousel = brandDisplayFormatCounts.carousel || 0
        clonedBrand.analytics.brandDisplayFormatCounts.dynamic = brandDisplayFormatCounts.dpa || 0
        clonedBrand.analytics.brandDisplayFormatCounts.other = brandDisplayFormatCounts.page_like || 0

        return clonedBrand
      }).filter(brand => brand && (textSearch.length > 0 ? brand.name.toLowerCase().includes(textSearch.toLowerCase()) : true)) // Filter out undefined brands (orphaned references)
    }, (arg1, arg2, arg3) => {
      // Update memoized value based on folderId + last_changed + text search
      return `${arg1}-${arg2}-${arg3}`
    }),
    getFolderByPageId(pageId) {
      return this.getFolders.find(folder => folder?.spyderBrands?.includes(pageId))?.name || 'Default Folder'
    },
    showChangeBrandFolderModal(brand) {
      this.changeFolderBrand = brand
      this.showChangeFolderModal = true
    },
    attachTransitionListeners() {
      // Attach onTransitionEnd event listener to each collapsible folder
      this.$nextTick(() => {
        for (let i = 0; i <= this.getFolders.length; i++) {
          const folder = this.$refs?.[`brand-folder-content-${i}`]?.[0]
          if (!folder) continue
          folder.addEventListener('transitionend', () => {
            if (this.folderExpanded[i]) {
              folder.style.overflow = 'visible'
            }
          })
        }
      })
    },
    removeTransitionListeners() {
      // Remove onTransitionEnd event listener from each collapsible folder
      for (let i = 0; i <= this.getFolders.length; i++) {
        const folder = this.$refs?.[`brand-folder-content-${i}`]?.[0]
        if (!folder) continue
        folder.removeEventListener('transitionend', () => {
          if (this.folderExpanded[i]) {
            folder.style.overflow = 'visible'
          }
        })
      }
    },
    async handleScrollTo(folderId, pageIds) {
      this.lastAddedBrands = [...pageIds]
      this.$nextTick(() => {
        const folderElement = this.$refs[`folder-${folderId}`][0]
        if (folderElement && pageIds.length > 0) {
          folderElement.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    async handleAddSpyder() {
      await this.fetchBrandAnalytics() // Make sure to re-fetch analytics when adding a spyder
      this.last_changed = +new Date()
      this.pollUnprocessedBrands()
    },
    async removeSpyderBrand(brand) {
      try{
        this.loadingMenu = brand?.subscription?.pageId || brand?.adLibraryId
        const brandPageId = brand.subscription.pageId || brand.adLibraryId
        const spyderFolder = this.getFolderByPageId(brandPageId) === 'Default Folder' ? null : this.getFolders.find(folder => folder?.spyderBrands?.includes(brand.subscription.pageId))

        const removePageIds = [{ pageId: brandPageId, folderId: spyderFolder?.id }]
        await ForeplayAPI.Spyder.removeTrackedBrands(removePageIds)
        this.SET_SPYDER_SUBSCRIPTIONS(this.getSpyderSubscriptions.filter(subscriptions => subscriptions.subscription.pageId !== brandPageId))
        const isDefaultFolder = spyderFolder === null
        if (isDefaultFolder) {
          console.log('Default folder spyder change')
          await this.fetchSpyderSubscriptions({ignoreDexie: true})
          this.fetchFolders()
          this.last_changed = +new Date()
          return
        }
        console.log("Specific folder spyder change")
        // updating folders
        const updatePayload = spyderFolder.spyderBrands.filter(brandId => brandId !== brandPageId)
        // FirebaseAPI.Folders.update(spyderFolder.id, { spyderBrands: updatePayload })
        this.SET_FOLDERS(this.getFolders.map(folder => folder.id === spyderFolder.id ? { ...folder, spyderBrands: updatePayload } : folder))
        this.last_changed = +new Date()
      }
      catch (error) {
        console.error(error)
      }
      finally {
        this.loadingMenu = null
      }
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Spyder'
  }
}
</script>

<style scoped>
/* Hide the last-checked column on narrower viewports */
.brand-grid-template {
  display: grid;
  grid-template-columns: 250px 146px 180px 300px 1fr 32px;
  align-items: center;
}

@media (max-width: 1640px) {
  .last-checked-column {
    display: none;
  }

  .brand-grid-template {
    grid-template-columns: 250px 180px 300px 1fr 32px;
  }
}
@media (max-width: 1500px) {
  .data-since-column {
    display: none;
  }
  .brand-grid-template {
    grid-template-columns: 250px 300px 1fr 32px;
  }
}

.folder-header-grid-template {
  display: grid;
  grid-template-columns: 220px 235px 1fr;
  align-items: center;
}

.ad-status-grid-template {
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
}

.format-breakdown-grid-template {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
}

.brand-info-tooltip {
  position: absolute;
  left: calc(100% + 6px);
  top: -9px;
}

.brand-folder-content {
  transition: height 200ms ease-in-out;
}

.brand-menu-button {
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}

.brand-menu-button.active {
  background-color: #ECEFF3;
}

.brand-menu-button.inactive:hover {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.brand-options {
  position: absolute;
  right: calc(100% + 4px);
  top: 50%;
  transform: translateY(-50%);
  width: 105px;
  z-index: 50;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}

.v-enter-active,
.v-leave-active {
  transition: opacity 150ms ease-in-out;
}

.v-enter-from,
.v-enter,
.v-leave-to {
  opacity: 0;
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
}

.option-enter-active,
.option-leave-active {
  transition: opacity 100ms ease-in-out;
}

.option-enter-from,
.option-enter,
.option-leave-to {
  opacity: 0;
}

.option-enter-to,
.option-leave-from {
  opacity: 1;
}

.new-brand-tag {
  padding: 2px 4px;
  background-color: #FDF2F1;
  color: #AB2D27;
  border-radius: 4px;
}

.search-container {
  position: relative;
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #EBEFF3;
  background: linear-gradient(90deg, rgba(76, 76, 82, var(--gradient-opacity)) 0%, rgba(52, 52, 59, var(--gradient-opacity)) 100%);
  transition: box-shadow 100ms ease-in-out, --gradient-opacity 100ms ease-in-out;
  width: 100%;
  max-width: 528px;
}
.search-input {
  color: black;
  transition: color 100ms ease-in-out;

  /* Remove default browser styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  padding: 0;
  margin: 0;
  border: none;
  color: inherit;
  background-color: transparent;
  outline: none;

  /* typography */
  font-feature-settings: 'ss01' on, 'cv10' on, 'liga' off, 'calt' off;
  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.search-input:focus {
  /* Remove default browser styles */
  outline: none;
  border: none;
  box-shadow: none;
}

.search-input-container:hover, .search-input-container:focus-within {
  box-shadow: 0px 1px 2px 0px rgba(32, 36, 50, 0.08), 0px 0px 0px 1px #808899;
}

/* Tab Styles */
.spyder-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.spyder-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.spyder-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}
</style>
